/**
 * Estado do pedido em edição (v6): cabeçalho, itens, frete, outras despesas e parcelas.
 * Tudo fica na tela até "Salvar rascunho", que grava numa só transação (D13). Usado pelo
 * Pedido de Venda e, na v7, pela Venda Direta.
 */
import {
  lineValue,
  mergeSaleEntry,
  salesOrderTotalsPreview,
  toFormMoney,
  toFormNumber,
  type SaleDraftItem,
  type SalesOrder,
} from "@cafegestor/shared";
import { useCallback, useMemo, useState } from "react";
import { toDecimal } from "@/lib/decimal";
import { today } from "../../estoque/lib";

export type SaleHeader = {
  customerId: string;
  customerLabel: string;
  salespersonId: string;
  salespersonLabel: string;
  warehouseId: string;
  paymentMethodId: string;
  orderDate: string;
  notes: string;
  loadId: string;
};

export type InstallmentRow = { key: number; dueDate: string; amount: string };

export type SaleDraftState = {
  header: SaleHeader;
  items: SaleDraftItem[];
  freight: string;
  other: string;
  installments: InstallmentRow[];
};

/** Itens do pedido gravado → itens da tela. */
export function itemsFromOrder(o: SalesOrder): SaleDraftItem[] {
  return (o.items ?? []).map((i) => ({
    key: `srv-${i.id}`,
    productId: i.productId,
    productCode: i.productCode,
    productName: i.productName,
    unitId: i.unitId,
    unitCode: i.unitCode,
    quantity: i.quantity,
    // no preço da tabela: segue a tabela; preço digitado é mantido
    unitPrice:
      i.listPrice !== null && Number(i.unitPrice) === Number(i.listPrice) ? null : i.unitPrice,
    listPrice: i.listPrice,
    maxDiscountPercent: i.maxDiscountPercent,
    discount: i.discount,
    notes: i.notes ?? null,
  }));
}

export function stateFromOrder(o: SalesOrder | null): SaleDraftState {
  if (!o) {
    return {
      header: {
        customerId: "",
        customerLabel: "",
        salespersonId: "",
        salespersonLabel: "",
        warehouseId: "",
        paymentMethodId: "",
        orderDate: today(),
        notes: "",
        loadId: "",
      },
      items: [],
      freight: "",
      other: "",
      installments: [],
    };
  }
  return {
    header: {
      customerId: o.customerId,
      customerLabel: o.customerName,
      salespersonId: o.salespersonId ?? "",
      salespersonLabel: o.salespersonName ?? "",
      warehouseId: o.warehouseId,
      paymentMethodId: o.paymentMethodId ?? "",
      orderDate: o.orderDate,
      notes: o.notes ?? "",
      loadId: o.loadId ?? "",
    },
    items: itemsFromOrder(o),
    freight: Number(o.freightTotal) === 0 ? "" : toFormMoney(o.freightTotal),
    other: Number(o.otherTotal) === 0 ? "" : toFormMoney(o.otherTotal),
    installments: (o.installments ?? []).map((p, n) => ({
      key: n + 1,
      dueDate: p.dueDate,
      amount: toFormMoney(p.amount),
    })),
  };
}

/** Texto de quantidade para a tabela da tela. */
export const qtyText = (v: string) => toFormNumber(v);

export function useSaleDraft(order: SalesOrder | null) {
  const [state, setState] = useState<SaleDraftState>(() => stateFromOrder(order));
  const [dirty, setDirty] = useState(false);
  const change = useCallback((fn: (s: SaleDraftState) => SaleDraftState) => {
    setState(fn);
    setDirty(true);
  }, []);

  const preview = useMemo(
    () =>
      salesOrderTotalsPreview({
        lineValues: state.items.map((i) => {
          const price = i.unitPrice ?? i.listPrice;
          return price === null ? null : lineValue(i.quantity, price);
        }),
        discounts: state.items.map((i) => i.discount),
        freight: toDecimal(state.freight, "dinheiro"),
        other: toDecimal(state.other, "dinheiro"),
      }),
    [state.items, state.freight, state.other],
  );

  return {
    state,
    dirty,
    preview,
    setHeader: (patch: Partial<SaleHeader>) =>
      change((s) => ({ ...s, header: { ...s.header, ...patch } })),
    setFreight: (v: string) => change((s) => ({ ...s, freight: v })),
    setOther: (v: string) => change((s) => ({ ...s, other: v })),
    setInstallments: (rows: InstallmentRow[]) => change((s) => ({ ...s, installments: rows })),
    /** inclui (ou soma, D10); o mesmo rascunho nunca entra duas vezes */
    addItem: (item: SaleDraftItem) => {
      const r = mergeSaleEntry(state.items, item);
      if (r.outcome !== "repetido")
        change((s) => ({ ...s, items: mergeSaleEntry(s.items, item).items }));
      return r;
    },
    replaceItem: (key: string, item: SaleDraftItem) =>
      change((s) => ({ ...s, items: s.items.map((i) => (i.key === key ? item : i)) })),
    removeItem: (key: string) => {
      const index = state.items.findIndex((i) => i.key === key);
      const removed = state.items[index];
      change((s) => ({ ...s, items: s.items.filter((i) => i.key !== key) }));
      return removed ? { removed, index } : null;
    },
    restoreItem: (item: SaleDraftItem, index: number) =>
      change((s) => {
        if (s.items.some((i) => i.key === item.key)) return s;
        const items = [...s.items];
        items.splice(Math.min(index, items.length), 0, item);
        return { ...s, items };
      }),
    reset: (o: SalesOrder | null) => {
      setState(stateFromOrder(o));
      setDirty(false);
    },
  };
}

export type SaleDraft = ReturnType<typeof useSaleDraft>;

/** Corpo para a API (o mesmo para criar e para salvar o rascunho; e para a Venda Direta). */
export function bodyOf(draft: { state: SaleDraftState }, receivable: boolean | null) {
  const { header, items, freight, other, installments } = draft.state;
  return {
    customerId: header.customerId,
    loadId: header.loadId,
    salespersonId: header.salespersonId,
    warehouseId: header.warehouseId,
    paymentMethodId: header.paymentMethodId,
    orderDate: header.orderDate,
    notes: header.notes,
    items: items.map((i: SaleDraftItem) => ({
      productId: i.productId,
      unitId: i.unitId,
      quantity: i.quantity,
      unitPrice: i.unitPrice ?? "",
      discount: i.discount,
      notes: i.notes ?? "",
    })),
    freight,
    other,
    installments:
      receivable === false
        ? []
        : installments.map((r) => ({ dueDate: r.dueDate, amount: r.amount })),
  };
}
